import React, { useEffect } from 'react';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
// Components
import ErrorMessage from '../../../components/error-message';
import { Typobold } from '../../../components/typobold';
import Listing from '../../../components/listing';
import EnhancedForm from '../../../components/form';
import { FidStep } from '.';
// Modules
import { Company, FidRewardType, FidType, Program, StrategyType } from '../../../redux/reducers/api-types';
import { actions as companyActions, types as companyTypes } from '../../../redux/reducers/companies';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
// Ressources
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { FormDatas, FormPickerDatas } from '../../../local-types';
import withGlobalStyles from '../../../utils/global-styles';
import { TooltipButton } from '../../../components/tooltip-button';
import { LCAlert } from '../../../components/alert';
import MainLoading from '../../../components/main-loading';
import { Anchor } from '../../../components/anchor';
import CompanyFid from './company-fid';
import { clsx } from '../../../utils/htmlUtils';

interface FidFormProps extends WithTranslation {
    globalClasses: any;
    company: Company; // need id and uuid from company
    rightCheck: AccountRight;
    getProgramError: ErrorPayload;
    createProgramError: ErrorPayload;
    isGettingCompanyFid: boolean;
    isCreatingFidProgram: boolean;
    getFidProgram: (id, uuid, parentUuid?) => any;
    createFidProgram: (id, payload, callback) => any;
}

const FidForm = (props: FidFormProps): JSX.Element => {
    const {
        t,
        globalClasses,
        company,
        rightCheck,
        isGettingCompanyFid,
        isCreatingFidProgram,
        getProgramError,
        createProgramError,
        getFidProgram,
        createFidProgram,
    } = props;

    let formRef: any;
    let alertStepRef: any = React.createRef();
    let formStepTmp: any;

    const [steps, setSteps] = React.useState<any[]>([]);

    const canReadFid: boolean = UserUtils.checkRights({
        name: rightCheck,
        mode: RightRank.R,
    });
    const canWriteFid: boolean = UserUtils.checkRights({
        name: rightCheck,
        mode: RightRank.W,
    });

    useEffect(() => {
        if (canReadFid && company.id && company.uuid) {
            getFidProgram(company.id, company.uuid);
        }
    }, [company.id, company.uuid, getFidProgram, canReadFid]);

    const generateTypeOptions = (): FormPickerDatas[] => {
        const typeOptions: FormPickerDatas[] = [
            {
                id: FidType.MONTH,
                name: t('communication.fid.form.type.monthly'),
            },
            {
                id: FidType.TARGET,
                name: t('communication.fid.form.type.target'),
            },
        ];
        return typeOptions;
    };

    const generateRewardOptions = (): FormPickerDatas[] => {
        const rewardOptions: FormPickerDatas[] = [
            {
                id: FidRewardType.COUPON,
                name: t('communication.fid.form.step.reward.coupon'),
            },
            // {
            //     id: FidRewardType.PARTNERSHIP,
            //     name: t('communication.fid.form.step.reward.partnership'),
            // },
            {
                id: FidRewardType.MAIL,
                name: t('communication.fid.form.step.reward.mail'),
            },
        ];
        return rewardOptions;
    };

    const generateCouponOptions = (): FormPickerDatas[] => {
        const couponOptions: FormPickerDatas[] = [
            {
                id: StrategyType.FIXED,
                name: t('communication.fid.form.step.strategy.fixed'),
            },
            {
                id: StrategyType.PERCENT,
                name: t('communication.fid.form.step.strategy.percent'),
            },
        ];
        return couponOptions;
    };

    const handleDeleteStep = (index: number) => (e) => {
        steps.splice(index, 1);
        setSteps([...steps]);
    };

    const generateFidInputs = (): FormDatas[] => {
        const form = 'form_step';
        const inputs: FormDatas[] = [
            {
                title: t('communication.fid.program_details'),
                datas: [
                    {
                        id: 'start_date',
                        label: t('communication.fid.form.enable'),
                        type: 'date',
                        required: true,
                        inputsAttributs: { min: new Date().toISOString().split('T')[0] },
                    },
                    { id: 'no_end_date', label: t('communication.fid.form.no_disable'), type: 'boolean' },
                    {
                        id: 'end_date',
                        label: t('communication.fid.form.disable'),
                        type: 'date',
                        linked: { id: 'no_end_date', value: false },
                        required: true,
                        linkDate: { id: 'start_date', type: 'min', value: 1, next: true, isFullDate: false },
                    },
                    { id: 'name', label: t('common:form.lastname'), required: true },
                    {
                        id: 'type',
                        label: t('communication.fid.form.type.label'),
                        options: generateTypeOptions(),
                        noEmpty: true,
                        required: true,
                    },
                ],
            },
            {
                title: t('communication.fid.step_details'),
                datas: [
                    {
                        id: 'reward_type',
                        label: t('communication.fid.form.step.reward.label'),
                        options: generateRewardOptions(),
                        inputsAttributs: { form },
                        noEmpty: true,
                        required: true,
                    },
                    {
                        id: 'threshold',
                        label: t('communication.fid.form.ride_count'),
                        inputsAttributs: { step: 1, form },
                        type: 'number',
                        required: true,
                    },
                    // {
                    //     id: 'mail',
                    //     label: t('communication.fid.form.step.mail'),
                    //     options: this.generateMailOptions(),
                    //     inputsAttributs: { form },
                    //     noEmpty: true,
                    //     required: true,
                    // },
                    {
                        id: 'strategy',
                        label: t('communication.fid.form.type.label'),
                        linked: { id: 'reward_type', value: 'coupon' },
                        options: generateCouponOptions(),
                        inputsAttributs: { form },
                        noEmpty: true,
                        required: true,
                    },
                    {
                        id: 'value',
                        label: t('communication.fid.form.step.value'),
                        linked: { id: 'reward_type', value: 'coupon' },
                        required: true,
                        numberNegative: false,
                        inputsAttributs: { step: 1, form },
                        type: 'number',
                    },
                    {
                        id: 'validity',
                        label: t('communication.fid.form.step.validity'),
                        linked: { id: 'reward_type', value: 'coupon' },
                        required: true,
                        numberNegative: false,
                        inputsAttributs: { step: 1, form },
                        type: 'number',
                    },
                    {
                        id: 'link',
                        label: t('communication.fid.form.step.link'),
                        linked: { id: 'reward_type', value: 'partnership' },
                        inputsAttributs: { form },
                        required: true,
                        type: 'url',
                    },
                    {
                        id: 'my_submit',
                        label: 'my_submit',
                        type: 'customButton',
                        icon: 'add',
                        inputsAttributs: { form },
                    },
                    {
                        id: 'company',
                        label: 'test',
                        customView: (val, handleChange) => {
                            return (
                                <Listing
                                    datas={steps?.map((step, index) => {
                                        return (
                                            <div
                                                key={`${step.threshold} ${step.reward_type} ${index}`}
                                                style={{
                                                    flex: 'inherit',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <FidStep step={step} />
                                                <TooltipButton title={t('common:delete')}>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleDeleteStep(index)}
                                                        className={clsx(
                                                            globalClasses.btnAutoWidth,
                                                            globalClasses.btnDanger,
                                                        )}
                                                    >
                                                        <Icon fontSize="small">delete</Icon>
                                                    </Button>
                                                </TooltipButton>
                                            </div>
                                        );
                                    })}
                                />
                            );
                        },
                    },
                ],
            },
        ];
        return inputs;
    };

    const handleSubmit = (formData: any, event): void => {
        // console.log('Creating program fid');

        const formStep = formStepTmp;

        if (!steps || steps.length < 1) {
            alertStepRef.showAlert();
            return;
        }

        alertStepRef.closeAlert();
        const payload: Program = {
            id_company: company.uuid,
            start_date: formStep.start_date,
            name: formStep.name,
            type: formStep.type,
            steps,
        };

        if (!formStep.no_end_date) {
            payload.end_date = formStep.end_date;
        }
        // console.log(payload);

        createFidProgram(company.id, payload, () => {
            steps.splice(0, steps.length);
            formRef.setValues([
                { name: 'name', value: '' },
                { name: 'start_date', value: '' },
                { name: 'no_end_date', value: false },
                { name: 'end_date', value: '' },
                { name: 'type', value: FidType.MONTH },
                { name: 'threshold', value: '' },
                { name: 'reward_type', value: FidRewardType.COUPON },
                { name: 'strategy', value: StrategyType.FIXED },
                { name: 'value', value: '' },
                { name: 'validity', value: '' },
                { name: 'link', value: '' },
            ]);
        });
    };

    const handleSubmitStep = (e): void => {
        e.preventDefault();
        const formStep = formStepTmp;
        let newStep: any = {
            threshold: formStep.threshold,
            // mail: formStep.mail,
            reward_type: formStep.reward_type,
        };
        if (formStep.reward_type === FidRewardType.COUPON) {
            newStep = {
                ...newStep,
                strategy: formStep.strategy,
                value: formStep.value,
                validity: formStep.validity,
            };
        } else if (formStep.reward_type === FidRewardType.PARTNERSHIP) {
            newStep = {
                ...newStep,
                link: formStep.link,
            };
        }

        // console.log('=>', newStep);
        steps.push(newStep);
        formRef.setValues([
            { name: 'name', value: formStep.name },
            { name: 'start_date', value: formStep.start_date },
            { name: 'no_end_date', value: formStep.no_end_date },
            { name: 'end_date', value: formStep.end_date },
            { name: 'type', value: formStep.type },
            { name: 'threshold', value: '' },
            { name: 'reward_type', value: FidRewardType.COUPON },
            { name: 'strategy', value: StrategyType.FIXED },
            { name: 'value', value: '' },
            { name: 'validity', value: '' },
            { name: 'link', value: '' },
        ]);
    };

    const onChange = (formStep: any) => {
        formStepTmp = formStep;
    };

    const renderFidConf = () => {
        if (!canWriteFid) {
            return null;
        }

        return (
            <>
                <form onSubmit={handleSubmitStep} id="form_step">
                    <input type="submit" hidden value="Ok" />
                </form>
                <EnhancedForm
                    style={{ paddingLeft: 0 }}
                    payload={{}}
                    inputs={generateFidInputs()}
                    onSubmit={handleSubmit}
                    onChange={onChange}
                    loader={isCreatingFidProgram}
                    moreView={
                        <>
                            <ErrorMessage payload={createProgramError} />
                            <LCAlert
                                onRef={(ref) => (alertStepRef = ref)}
                                details={t('communication.fid.form.step.no_step_error')}
                                type="error"
                            />
                        </>
                    }
                    onRef={(ref) => {
                        formRef = ref;
                    }}
                />
            </>
        );
    };

    const renderFidPrograms = () => {
        if (!canReadFid) {
            return null;
        }

        const { fid_programs } = company;

        let programsFid = fid_programs;
        if (!isGettingCompanyFid && programsFid?.length > 0) {
            if (programsFid.find((program) => program.id_company === company.uuid)) {
                programsFid = programsFid.filter((program) => program.id_company === company.uuid);
            }
        }

        return (
            <div style={{ marginTop: 30 }}>
                <Anchor id="fid_conf" />
                <ErrorMessage payload={getProgramError} />
                <Typobold>{t('communication.fid.title')}</Typobold>
                {isGettingCompanyFid ? (
                    <MainLoading center />
                ) : programsFid?.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                        {programsFid.map((program, index) => (
                            <div key={index} style={{ flex: '1 1 calc(50% - 10px)', minWidth: '300px' }}>
                                <CompanyFid company={company} program={program} rightCheck={rightCheck} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div>{t('communication.fid.no_program')}</div>
                )}
            </div>
        );
    };

    return (
        <>
            {renderFidConf()}
            {renderFidPrograms()}
        </>
    );
};

const mapStateToProps = (state) => ({
    getProgramError: errorSelectors.getProgramError(state),
    createProgramError: errorSelectors.createProgramError(state),
    isGettingCompanyFid: pendingSelectors.isGettingCompanyFid(state),
    isCreatingFidProgram: pendingSelectors.isPending(state, companyTypes.CREATE_FID_PROGRAM),
});

const mapDispatchToProps = {
    getFidProgram: companyActions.getFidProgram,
    createFidProgram: companyActions.createFidProgram,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withGlobalStyles(withTranslation(['company', 'common'])(FidForm)));
